import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { useLoader } from '../api/useLoader';
import {
  BrukerrollerDto,
  PraksisaktivitetStatusDto,
  PraksisklasseDto,
  RegistreringsStatusDto,
  StudenterMedKlasseDto,
  StudentregistreringDto,
  StudentregistreringPutDto,
} from '../api/generated/studiested-generated-api';
import Button from '../components/button';
import { Checkbox } from '@mui/material';
import translations from './registrering.i18n';
import * as css from './registrering.module.scss';
import Spinner from '../components/spinner/spinner';
import { UhaUserContext } from '../user/UhaUserStore';
import { NavLink } from 'react-router-dom';
import Dropdown, { DropdownOption } from '../components/input/dropdown';
import { ConfirmationDialog } from '../components/dialog';
import { NotificationFunctionContext } from '../components/notifications/notificationProvider';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import useUnsavedChangesPrompt from '../hooks/useUnsavedChangesPrompt';
import { useActivityContextUHA } from './activitycontexts';
import useAddErrorNotificationCallback from '../hooks/useAddErrorNotificationCallback';
import { useFormatMessageFor } from '../i18n/translation';

const RegistreringWrapper = () => {
  const { praksisaktivitet } = useActivityContextUHA();
  const { id: aktivitetsId } = praksisaktivitet;
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { addSuccessNotification } = useContext(NotificationFunctionContext);
  const errorNotificationCallback = useAddErrorNotificationCallback();
  const { data, reload } = useLoader(
    () =>
      praksiskalenderApi.praksisaktivitetIdRegistreringGet({
        pathParams: { id: aktivitetsId },
      }),
    [aktivitetsId],
  );

  const labels = useFormatMessageFor(translations);

  if (!data) {
    return <Spinner />;
  }

  const save = (studenterMedKlasser: StudentregistreringPutDto[]) => {
    praksiskalenderApi
      .praksisaktivitetIdRegistreringPut({
        pathParams: { id: aktivitetsId! },
        studentregistreringPutWrappedDto: { wrapped: studenterMedKlasser },
      })
      .then(() => {
        addSuccessNotification(labels.lagret);
        reload();
      })
      .catch(errorNotificationCallback);
  };

  return <Registrering studentregistreringer={data} save={save} />;
};

const mapStudentIdsToClasses = (selectedClasses: {
  [studentId: string]: string | undefined;
}) =>
  Object.entries(selectedClasses).map(([studentId, klasseId]) => ({
    studentId,
    klasseId,
  }));

enum RegisterEvent {
  REGISTER_ALL = 'REGISTER_ALL ',
  REGISTER_SELECTED = 'REGISTER_SELECTED',
  REMOVE_ALL = 'REMOVE_ALL',
  REMOVE_SELECTED = 'REMOVE_SELECTED',
  REMOVE_OR_REGISTER = 'REMOVE_OR_REGISTER',
}

const Registrering: React.FC<
  React.PropsWithChildren<{
    studentregistreringer: StudentregistreringDto;
    save: (studenterMedKlasser: StudentregistreringPutDto[]) => void;
  }>
> = ({ studentregistreringer, save }) => {
  const { praksisaktivitet } = useActivityContextUHA();
  const { klasse, publisertTilFs, emne, praksis, status } = praksisaktivitet;
  const { uhaUser: bruker } = useContext(UhaUserContext);
  const { muligeKlasser, studenter } = studentregistreringer;

  const previouslyAssignedStudents = React.useMemo(
    () =>
      studenter.filter(
        (student) => student.status !== RegistreringsStatusDto.IkkeRegistrert,
      ),
    [studenter],
  );

  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<{
    [studentId: string]: string | undefined;
  }>({});
  const [activeCol, setActiveCol] = useState<number>(0);
  const [sortedStudents, setSortedStudents] =
    useState<StudenterMedKlasseDto[]>();
  const [studentSearch, setStudentSearch] = useState<string>('');
  const [fsClassSearch, setFsClassSearch] = useState<string>('');
  const [classSearch, setClassSearch] = useState<string>('');
  const [selectAllActive, setSelectAllActive] = useState<boolean>(false);
  const [
    showRemoveOrRegisterStudentNotification,
    setShowRemoveOrRegisterStudentNotification,
  ] = useState<RegisterEvent | undefined>(undefined);
  const [studentsToRegisterOrRemove, setStudentsToRegisterOrRemove] = useState<
    StudentregistreringPutDto[]
  >([]);

  const { locale } = useIntl();
  const labels = useFormatMessageFor(translations);

  const onSelectionChange = (studentId: string, selected: boolean) =>
    setSelectedStudentIds(
      selected
        ? selectedStudentIds.concat(studentId)
        : selectedStudentIds.filter((id) => id !== studentId),
    );

  const onSelectAllChange = (selectAll: boolean) => {
    const filteredAndSortedStudentIds = filteredAndSortedStudents.map(
      (student) => student.id!,
    );

    const selectedIdsNotFiltered = selectedStudentIds.filter(
      (id) => !filteredAndSortedStudentIds.includes(id),
    );

    setSelectAllActive(selectAll);
    setSelectedStudentIds(
      selectAll
        ? filteredAndSortedStudentIds.concat(selectedIdsNotFiltered)
        : selectedIdsNotFiltered,
    );
  };

  const onClassChange = (studentId: string, klasseId: string) => {
    if (
      previouslyAssignedStudents.some(
        (previouslyAssignedStudent) =>
          previouslyAssignedStudent.id === studentId &&
          previouslyAssignedStudent.klasse?.id === klasseId,
      )
    ) {
      // If student is previously assigned, ignore change, if it is back to initial value
      if (selectedClasses.hasOwnProperty(studentId)) {
        setSelectedClasses(
          Object.entries(selectedClasses).reduce(
            (acc, [key, value]) => (studentId === key ? acc : { [key]: value }),
            {},
          ),
        );
      }
    } else {
      setSelectedClasses({
        ...selectedClasses,
        [studentId]: klasseId === labels.fjern ? undefined : klasseId,
      });
    }
  };

  const removeOrRegisterStudents = (
    studentIdsToRemoveOrRegister: string[],
    event: RegisterEvent,
  ) => {
    const remove =
      event === RegisterEvent.REMOVE_SELECTED ||
      event === RegisterEvent.REMOVE_ALL;

    const studentsMappedByClass = studentIdsToRemoveOrRegister.map(
      (studentId) => ({
        studentId,
        klasseId: remove ? undefined : klasse.id,
      }),
    );

    if (
      previouslyAssignedStudents.some(
        (student) =>
          student.id && studentIdsToRemoveOrRegister.includes(student.id),
      )
    ) {
      setStudentsToRegisterOrRemove(studentsMappedByClass);
      setShowRemoveOrRegisterStudentNotification(event);
    } else {
      // If none of the new changes concerns previously assigned (saved) students, don't show warning
      save(studentsMappedByClass);
    }
  };

  const onRegister = (event: RegisterEvent) => {
    switch (event) {
      case RegisterEvent.REMOVE_ALL:
        removeOrRegisterStudents(
          filteredAndSortedStudents
            .filter(
              (student) => student.id && student.klasse?.id === klasse.id, // Filter only students assigned to current class
            )
            .map((student) => student.id!),
          event,
        );
        break;
      case RegisterEvent.REGISTER_ALL:
        removeOrRegisterStudents(
          filteredAndSortedStudents.map((student) => student.id!),
          event,
        );
        break;
      case RegisterEvent.REMOVE_SELECTED:
      case RegisterEvent.REGISTER_SELECTED:
        removeOrRegisterStudents(selectedStudentIds, event);
    }
  };

  const onSave = () => {
    const changedClasses = Object.entries(selectedClasses).reduce(
      (acc: StudentregistreringPutDto[], [studentId, klasseId]) =>
        !!previouslyAssignedStudents.find(
          (student) => student.id === studentId && student.klasse !== klasseId,
        )
          ? acc.concat({ studentId, klasseId })
          : acc,
      [],
    );

    if (changedClasses.length > 0) {
      setShowRemoveOrRegisterStudentNotification(
        RegisterEvent.REMOVE_OR_REGISTER,
      );
      setStudentsToRegisterOrRemove(changedClasses);
    } else {
      fetchClassesAndSave();
    }
  };

  const fetchClassesAndSave = () => {
    // Save edited students to assigned classes
    save(mapStudentIdsToClasses(selectedClasses));
  };

  const SelectAllCheckbox = () => (
    <div
      title={labels.selectAll}
      className={css.checkbox}
      data-cy={'select-all-checkbox'}
    >
      <Checkbox
        color="secondary"
        size="small"
        checked={selectAllActive}
        style={{
          padding: '0',
        }}
        onClick={() => onSelectAllChange(!selectAllActive)}
      />
    </div>
  );

  const headers = [
    { title: '' },
    { title: labels.studenter },
    { title: '' },
    { title: labels.fsKlasse },
    { title: labels.klasse },
  ];

  const muligeKlasserOptions = useMuligeKlasserOptions(
    labels.fjern,
    muligeKlasser,
  );

  const canWrite =
    (bruker && bruker.hoeyesteRettighet !== BrukerrollerDto.Leserettighet) ||
    false;

  const handleDeleteConfirmationClose = (confirmed: boolean) => {
    if (confirmed) {
      save(studentsToRegisterOrRemove);
    }

    setShowRemoveOrRegisterStudentNotification(undefined);
    setStudentsToRegisterOrRemove([]);
  };

  const statusIsGjennomfoert = status
    ? status === PraksisaktivitetStatusDto.Gjennomfoert
    : false;

  const compare = Intl.Collator(locale || 'nb').compare;
  const sortStudents = (
    col: number,
    a: StudenterMedKlasseDto,
    b: StudenterMedKlasseDto,
  ) => {
    switch (col) {
      case 1:
        return compare(a.etternavn, b.etternavn);
      case 3:
        return compare(a.fsKlasse || '0', b.fsKlasse || '0');
      case 4:
        return compare(
          a.klasse?.presentertNavn || a.klasse?.bokstav || '0',
          b.klasse?.presentertNavn || b.klasse?.bokstav || '0',
        );
    }

    return 0;
  };

  const sortStudentsByActiveCol = (col: number, ascending: boolean) => {
    setSortedStudents(
      [...studenter].sort((a, b) =>
        ascending ? sortStudents(col, b, a) : sortStudents(col, a, b),
      ),
    );
  };

  const filteredAndSortedStudents = React.useMemo(
    () =>
      (sortedStudents || studenter).filter((student) => {
        const filterName =
          studentSearch.length === 0 ||
          `${student.fornavn} ${student.etternavn}`
            .toLowerCase()
            .indexOf(studentSearch) > -1;
        const filterFs =
          fsClassSearch.length === 0 ||
          (student.fsKlasse &&
            student.fsKlasse.toLowerCase().indexOf(fsClassSearch) > -1);
        const filterClass =
          classSearch.length === 0 ||
          (student.klasse &&
            (student.klasse.presentertNavn || student.klasse.bokstav)
              .toLowerCase()
              .indexOf(classSearch) > -1);

        return filterName && filterFs && filterClass;
      }),
    [studenter, sortedStudents, studentSearch, fsClassSearch, classSearch],
  );

  const getTableRows = () =>
    filteredAndSortedStudents.map((student) => (
      <RegistrationTableRow
        selected={selectedStudentIds.includes(student.id!)}
        key={student.id}
        valg={student.klasse?.presentertNavn || student.klasse?.bokstav}
        canWrite={canWrite}
        student={student}
        muligeKlasserOptions={muligeKlasserOptions}
        onSelectionChange={onSelectionChange}
        onKlasseChange={onClassChange}
        publisertTilFs={publisertTilFs}
        gjennomfoert={statusIsGjennomfoert}
        hideDropdownBorder={
          student.id !== undefined &&
          !Object.keys(selectedClasses).includes(student.id) &&
          student.klasse &&
          student.klasse.bokstav !== ''
        }
        headers={headers}
      />
    ));

  const canSortIndices = [1, 3, 4];
  const canSearchIndices = [1, 3, 4];

  const search = (index: number, value: string) => {
    switch (index) {
      case canSearchIndices[0]:
        setStudentSearch(value.toLowerCase());
        break;
      case canSearchIndices[1]:
        setFsClassSearch(value.toLowerCase());
        break;
      case canSearchIndices[2]:
        setClassSearch(value.toLowerCase());
    }
  };

  // If all filtered students are selected, make select all checkbox able to deselect all
  useEffect(() => {
    setSelectAllActive(
      filteredAndSortedStudents.every((student) =>
        selectedStudentIds.includes(student.id!),
      ),
    );
  }, [filteredAndSortedStudents, selectedStudentIds]);

  const getHeaders = () =>
    headers.map(({ title }, index) => (
      <TableCellTh
        key={title + index}
        colIndex={index}
        activeCol={index === activeCol}
        setActiveCol={setActiveCol}
        sort={sortStudentsByActiveCol}
        canSort={canSortIndices.includes(index)}
        canSearch={canSearchIndices.includes(index)}
        search={search}
      >
        {index === 0 && canWrite ? <SelectAllCheckbox /> : title}
      </TableCellTh>
    ));

  useUnsavedChangesPrompt(
    canWrite &&
      !statusIsGjennomfoert &&
      Object.keys(selectedClasses).length > 0,
  );

  const allAreSelected = useMemo(
    () => selectedStudentIds.length === 0,
    [selectedStudentIds],
  );

  return (
    <>
      <ConfirmationDialog
        submitLabel={labels.confirmEdit}
        isOpen={!!showRemoveOrRegisterStudentNotification}
        onClose={handleDeleteConfirmationClose}
        title={labels.removeStudentTitle}
        subtitle={`${emne.navn} ${emne.kode} - ${praksis.navn}`}
        dataCy={'change-student-praksisklasse-confirmation-dialog'}
      >
        <p>{labels.removeStudentInfo1}</p>
        <p>{labels.removeStudentInfo2}</p>
        <p>{labels.removeStudentInfo3}</p>
      </ConfirmationDialog>
      <Table>
        <TableHeader>
          <TableRow top>{getHeaders()}</TableRow>
        </TableHeader>
        <TableMain>{getTableRows()}</TableMain>
      </Table>
      <div className={css.buttonContainer}>
        {canWrite && !statusIsGjennomfoert && (
          <>
            <Button.Tertiary
              id={'remove-btn'}
              data-cy={'remove-button'}
              onClick={() =>
                onRegister(
                  allAreSelected
                    ? RegisterEvent.REMOVE_ALL
                    : RegisterEvent.REMOVE_SELECTED,
                )
              }
            >
              {allAreSelected
                ? `${labels.fjernAlle} ${
                    klasse.presentertNavn || klasse.bokstav
                  }`
                : labels.fjernValgte}
            </Button.Tertiary>
            <Button.Secondary
              id={'register-btn'}
              data-cy={'register-button'}
              onClick={() =>
                onRegister(
                  allAreSelected
                    ? RegisterEvent.REGISTER_ALL
                    : RegisterEvent.REGISTER_SELECTED,
                )
              }
            >
              {`${
                allAreSelected ? labels.registrerAlle : labels.registrerValgte
              } ${klasse.presentertNavn || klasse.bokstav}`}
            </Button.Secondary>
            <Button.Secondary
              id={'save-btn'}
              data-cy={'save-button'}
              disabled={Object.keys(selectedClasses).length === 0}
              onClick={onSave}
            >
              {labels.lagre}
            </Button.Secondary>
          </>
        )}
      </div>
    </>
  );
};

const useMuligeKlasserOptions = function (
  removeValg: string,
  muligeKlasser: PraksisklasseDto[],
): DropdownOption[] {
  return React.useMemo(() => {
    const muligeKlasserUI = muligeKlasser.map<DropdownOption>((klasse) => ({
      id: klasse.id,
      navn: klasse.presentertNavn || klasse.bokstav,
    }));
    muligeKlasserUI.push({
      id: removeValg,
      navn: removeValg,
    });
    return muligeKlasserUI;
  }, [removeValg, muligeKlasser]);
};

const RegistrationTableRow: React.FC<
  React.PropsWithChildren<{
    canWrite: boolean;
    valg?: string;
    student: StudenterMedKlasseDto;
    muligeKlasserOptions: DropdownOption[];
    onSelectionChange: (studentId: string, selected: boolean) => void;
    onKlasseChange: (studentId: string, klasseId: string) => void;
    publisertTilFs: boolean;
    gjennomfoert: boolean;
    hideDropdownBorder: boolean | undefined;
    headers: { title: string }[];
    selected: boolean;
  }>
> = React.memo(
  ({
    canWrite,
    valg,
    student,
    muligeKlasserOptions,
    onSelectionChange,
    onKlasseChange,
    publisertTilFs,
    gjennomfoert,
    hideDropdownBorder,
    headers,
    selected,
  }) => {
    const { fornavn, etternavn, klasse, id, fsKlasse, status } = student;

    const { formatMessage } = useIntl();
    const velg = formatMessage({ id: translations.velg.id });

    const avmeldt = status === RegistreringsStatusDto.Avmeldt;
    const ikkeRegistrert =
      status === RegistreringsStatusDto.IkkeRegistrert && publisertTilFs;

    let tabHeader = 0;
    return (
      <TableRow rowBackground={avmeldt || ikkeRegistrert ? 'alert' : 'default'}>
        <TableCellTd header={headers[tabHeader++].title}>
          {canWrite && (
            <div
              title={velg}
              className={css.checkbox}
              data-cy={'select-student-checkbox'}
            >
              <Checkbox
                color="secondary"
                size="small"
                disabled={!id}
                checked={selected}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  onSelectionChange(id!, event.target.checked);
                }}
                style={{
                  padding: '0',
                }}
              />
            </div>
          )}
        </TableCellTd>
        <TableCellTd header={headers[tabHeader++].title}>
          {id ? (
            <NavLink to={'/myinstitution/studentbrukerprofil/' + id}>
              {fornavn + ' ' + etternavn}
            </NavLink>
          ) : (
            fornavn + ' ' + etternavn
          )}
        </TableCellTd>
        <TableCellTd header={headers[tabHeader++].title}>
          {avmeldt || ikkeRegistrert ? <Notification status={status} /> : ''}
        </TableCellTd>
        <TableCellTd header={headers[tabHeader++].title}>
          {fsKlasse}
        </TableCellTd>
        <TableCellTd header={headers[tabHeader].title}>
          {canWrite && !gjennomfoert && id ? (
            <div className={css.classCell}>
              <Dropdown
                id={id}
                ariaLabel={'practice-class-select-dropdown'}
                options={muligeKlasserOptions}
                placeholder={velg}
                defaultValue={
                  klasse
                    ? {
                        id: klasse.id,
                        navn: klasse.presentertNavn || klasse.bokstav,
                      }
                    : undefined
                }
                onChange={(event: object, klasseOption: DropdownOption) => {
                  onKlasseChange(id, klasseOption && klasseOption.id);
                }}
                hideBorder={hideDropdownBorder}
                alert={avmeldt || ikkeRegistrert}
                dataCy={'praksisklasse-dropdown-input'}
              />
            </div>
          ) : (
            <span>{valg}</span>
          )}
        </TableCellTd>
      </TableRow>
    );
  },
);

const Notification: React.FC<
  React.PropsWithChildren<{ status: RegistreringsStatusDto }>
> = ({ status }) => {
  const labels = useFormatMessageFor(translations);

  const notificationMessage = useMemo(() => {
    switch (status) {
      case RegistreringsStatusDto.Avmeldt:
        return labels.avmeldtStudent;
      case RegistreringsStatusDto.IkkeRegistrert:
        return labels.ikkeRegistrertStudent;
      case RegistreringsStatusDto.Registrert:
        return '';
    }
  }, [labels, status]);

  return <>{notificationMessage}</>;
};

export default RegistreringWrapper;
